import React, { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import { useQuery } from 'react-query';
import { Helmet } from 'react-helmet-async';
import { useTranslation } from 'react-i18next';
import 'moment/locale/el';
import dynamicAPI from '../../api/dynamicAPI';
import { fileIconFor, buildDateTime, renderWithLinks } from '../../utils/sessionPresentation';
import '../../css/frontend/public-curation.css';

export default function PublicSession() {
    const { t, i18n } = useTranslation();
    const lang = i18n.language && i18n.language.startsWith('el') ? 'el' : 'en';
    const { publicId } = useParams();
    const [activeImg, setActiveImg] = useState(null);
    const [videoLoaded, setVideoLoaded] = useState(false);
    const [resName, setResName] = useState('');
    const [resEmail, setResEmail] = useState('');
    const [resSubmitting, setResSubmitting] = useState(false);
    const [resDone, setResDone] = useState(false);
    const [resError, setResError] = useState('');

    const { data, isLoading, isError } = useQuery(
        ['public-session', publicId],
        async () => await dynamicAPI('get', `/api/view/sessions/${publicId}`, {}),
        { retry: 0, enabled: !!publicId }
    );

    const session = data?.session;
    const spots = data?.spots || null;
    const reservation = data?.reservation || null;

    // Logged-in visitors get the internal view (which has the comments) instead
    // of the public participant page. We need the session's _id (returned by the
    // public endpoint) to build the admin link, so we redirect once it loads.
    const isLoggedIn = (() => {
        try { return !!JSON.parse(localStorage.getItem('userInfo') || 'null'); }
        catch { return false; }
    })();
    useEffect(() => {
        if (isLoggedIn && session?._id) {
            window.location.assign(`/admin/sessions/${session._id}/view`);
        }
    }, [isLoggedIn, session?._id]);

    const submitReservation = async (e) => {
        e.preventDefault();
        setResError('');
        const name = resName.trim();
        const email = resEmail.trim();
        if (!name || !email) {
            setResError(t('Please enter your name and email.'));
            return;
        }
        setResSubmitting(true);
        try {
            const res = await dynamicAPI('post', `/api/view/sessions/${publicId}/reserve`, { name, email });
            if (res?.success) { setResDone(true); }
            else { setResError(res?.error || t('Something went wrong. Please try again.')); }
        } catch (err) {
            setResError(err?.error || t('Something went wrong. Please try again.'));
        } finally {
            setResSubmitting(false);
        }
    };

    const imagesPath = data?.images_path || '';
    const images = session?.images || [];
    const hasTag = (img, tag) =>
        Array.isArray(img.tags) && img.tags.some((tg) => String(tg).toLowerCase() === tag);
    const poster =
        images.find((img) => img.featured) ||
        images.find((img) => hasTag(img, 'poster')) ||
        images[0] || null;
    const extras = images.filter(
        (img) => img !== poster && hasTag(img, 'extra public images')
    );
    const video = session?.showVideo === false ? null : session?.video;
    const dateTime = buildDateTime(session, lang);

    const formatMB = (bytes) => (bytes ? `${(bytes / (1024 * 1024)).toFixed(1)} MB` : '');

    const pageTitle = session?.title || t('Session');
    const metaDescription = (session?.description || '')
        .replace(/\s+/g, ' ').trim().slice(0, 200);
    const ogImage = poster ? `${imagesPath}${poster.path}` : '';
    const pageUrl = typeof window !== 'undefined' ? window.location.href : '';

    // While a logged-in visitor is being sent to the internal view, show a
    // placeholder instead of flashing the public page.
    if (isLoggedIn && !isError) {
        return (
            <div className="container py-5 text-center text-muted">
                {t('You are logged in — opening the internal view…')}
            </div>
        );
    }

    return (
        <div className="container py-4 mobile-hamburger-clear" style={{ maxWidth: 820, margin: '0 auto' }}>
            <Helmet>
                <title>{pageTitle}</title>
                {metaDescription && <meta name="description" content={metaDescription} />}
                <meta property="og:type" content="article" />
                <meta property="og:site_name" content="SpaceTime Community Lab" />
                <meta property="og:title" content={pageTitle} />
                {metaDescription && <meta property="og:description" content={metaDescription} />}
                {pageUrl && <meta property="og:url" content={pageUrl} />}
                {ogImage && <meta property="og:image" content={ogImage} />}
                <meta name="twitter:card" content={ogImage ? 'summary_large_image' : 'summary'} />
                <meta name="twitter:title" content={pageTitle} />
                {metaDescription && <meta name="twitter:description" content={metaDescription} />}
                {ogImage && <meta name="twitter:image" content={ogImage} />}
            </Helmet>

            {isLoading ? (
                <div className="text-center text-muted py-5">{t('Loading…')}</div>
            ) : isError || !session ? (
                <div className="text-center py-5">
                    <h4 className="fw-light">{t('This page is not available.')}</h4>
                    <p className="text-muted">{t('The link may be turned off or no longer exist.')}</p>
                </div>
            ) : (
                <>
                    <h1 className="fw-light mb-2">{session.title}</h1>
                    {dateTime && (
                        <div className="pc-datetime mb-3">
                            <div className="pc-dt-cell">
                                <span className="pc-dt-kicker">{dateTime.dateKicker}</span>
                                <span className="pc-dt-main">{dateTime.dateMain}</span>
                            </div>
                            {dateTime.timeMain && (
                                <div className="pc-dt-cell">
                                    <span className="pc-dt-kicker">{t('Time')}</span>
                                    <span className="pc-dt-main">{dateTime.timeMain}</span>
                                </div>
                            )}
                        </div>
                    )}

                    {poster && (
                        <div className="mb-4">
                            {(() => {
                                const src = `${imagesPath}${poster.path}`;
                                return (
                                    <img
                                        src={src}
                                        alt={session.title}
                                        onClick={() => setActiveImg(src)}
                                        style={{
                                            width: '100%', borderRadius: 10, cursor: 'zoom-in',
                                            display: 'block', background: '#f1f3f5',
                                        }}
                                    />
                                );
                            })()}
                        </div>
                    )}

                    {session.description && (
                        <p className="pc-description" style={{ whiteSpace: 'pre-wrap', lineHeight: 1.6, fontSize: '0.8rem', background: '#fff', padding: 25, borderRadius: 10 }}>
                            {renderWithLinks(session.description)}
                        </p>
                    )}

                    {Array.isArray(session.files) && session.files.length > 0 && (
                        <div className="pc-files mb-4">
                            <h3 className="pc-reserve-title">{t('Files')}</h3>
                            <ul className="pc-files-list" style={{ listStyle: 'none', padding: 0, margin: 0 }}>
                                {session.files.map((f) => {
                                    const Icon = fileIconFor(f.name, f.mimeType);
                                    return (
                                        <li key={f.id || f.path} style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '8px 0', borderBottom: '1px solid #eee' }}>
                                            <a href={`${imagesPath}${f.path}`} target="_blank" rel="noreferrer" download style={{ display: 'inline-flex', alignItems: 'center', gap: 12, minWidth: 0 }}>
                                                <Icon style={{ color: '#1e88e5', flex: '0 0 auto', fontSize: '2rem' }} aria-hidden="true" />
                                                <span>{f.label || f.name}</span>
                                            </a>
                                            {f.size != null && (
                                                <span className="text-muted" style={{ fontSize: '0.85rem' }}>
                                                    {f.size >= 1024 * 1024 ? `${(f.size / (1024 * 1024)).toFixed(1)} MB` : `${Math.round(f.size / 1024)} KB`}
                                                </span>
                                            )}
                                        </li>
                                    );
                                })}
                            </ul>
                        </div>
                    )}

                    {spots && (
                        <div className="pc-info">
                            <div className="pc-info-cell pc-info-spots">
                                {spots.full ? (
                                    <span className="pc-info-value pc-info-full">{t('Full')}</span>
                                ) : (
                                    <>
                                        <span className="pc-info-label">{t('Spots left')}</span>
                                        <span className="pc-info-value">{spots.left}</span>
                                    </>
                                )}
                            </div>
                        </div>
                    )}

                    {(extras.length > 0 || video) && (
                        <div className="mt-4 pc-extras">
                            {extras.map((img) => {
                                const src = `${imagesPath}${img.path}`;
                                return (
                                    <img
                                        key={img.id || img.path}
                                        className="pc-extra"
                                        src={src}
                                        alt={session.title}
                                        loading="lazy"
                                        onClick={() => setActiveImg(src)}
                                    />
                                );
                            })}

                            {video && (
                                videoLoaded ? (
                                    <video
                                        className="pc-video"
                                        src={`${imagesPath}${video.path}`}
                                        controls
                                        autoPlay
                                    />
                                ) : (
                                    <button
                                        type="button"
                                        className="pc-video-btn"
                                        onClick={() => setVideoLoaded(true)}
                                    >
                                        <span style={{ fontSize: '2.5rem', lineHeight: 1 }}>▶</span>
                                        <span style={{ fontSize: '0.8rem', opacity: 0.8 }}>
                                            {t('Click to play')}{video.size ? ` · ${formatMB(video.size)}` : ''}
                                        </span>
                                    </button>
                                )
                            )}
                        </div>
                    )}

                    {/* Reservation / sign-up form — kept at the very bottom of
                        the page so visitors read the session first, then sign up. */}
                    {reservation?.formEnabled && (
                        <div className="pc-reserve mt-4">
                            {resDone ? (
                                <div className="pc-reserve-panel">
                                    <h3 className="pc-reserve-title">{t('You are on the list')}</h3>
                                    <p className="pc-reserve-text">{t('Thanks! See you there.')}</p>
                                </div>
                            ) : (
                                <form className="pc-reserve-form" onSubmit={submitReservation}>
                                    <h3 className="pc-reserve-title">{t('Join this session')}</h3>
                                    <div className="pc-reserve-fields">
                                        <input
                                            type="text"
                                            className="pc-reserve-input"
                                            placeholder={t('Your name')}
                                            value={resName}
                                            onChange={(e) => setResName(e.target.value)}
                                            maxLength={120}
                                            required
                                        />
                                        <input
                                            type="email"
                                            className="pc-reserve-input"
                                            placeholder={t('Your email')}
                                            value={resEmail}
                                            onChange={(e) => setResEmail(e.target.value)}
                                            maxLength={180}
                                            required
                                        />
                                    </div>
                                    <button type="submit" className="pc-reserve-btn" disabled={resSubmitting}>
                                        {resSubmitting ? t('Sending…') : t('I will participate')}
                                    </button>
                                    {resError && <p className="pc-reserve-error">{resError}</p>}
                                </form>
                            )}
                        </div>
                    )}

                    {activeImg && (
                        <div
                            onClick={() => setActiveImg(null)}
                            style={{
                                position: 'fixed', inset: 0, background: 'rgba(0,0,0,.8)',
                                display: 'flex', alignItems: 'center', justifyContent: 'center',
                                zIndex: 9999, padding: 16, cursor: 'zoom-out',
                            }}
                        >
                            <img
                                src={activeImg}
                                alt=""
                                style={{ maxWidth: '95vw', maxHeight: '95vh', width: 'auto', height: 'auto', objectFit: 'contain', borderRadius: 8 }}
                            />
                        </div>
                    )}
                </>
            )}
        </div>
    );
}
